import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { Order, OrderStatus, Platform } from '@auto-oms/shared/types/order';
import type { ScanInboxResult } from '@auto-oms/shared/types/batch';
import { api, type ApiError } from '../lib/api';
import { pushToast } from '../lib/toast';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ChannelBadge } from '../components/ui/ChannelBadge';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ScanResultDialog } from '../components/ui/ScanResultDialog';
import { POPreviewDialog } from '../components/ui/POPreviewDialog';
import { ShipmentResultDialog } from '../components/ui/ShipmentResultDialog';
import { OrderDetailDialog } from '../components/ui/OrderDetailDialog';
import { formatDateTime, formatKRW } from '../lib/format';

interface LoaderData {
  orders: Order[];
}

export async function ordersLoader(): Promise<LoaderData> {
  return api.get<LoaderData>('/orders?limit=200');
}

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

function toLocalDateInit(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type CollectPlatform = 'ALL' | Extract<Platform, 'NAVER' | 'COUPANG'>;
type CollectPreset = 'today' | '3d' | '7d';
type ScanPreset = 'today' | 'yesterday' | '3d' | '7d';

const COLLECT_PRESET_LABEL: Record<CollectPreset, string> = {
  today: '오늘',
  '3d': '3일',
  '7d': '7일',
};
const SCAN_PRESET_LABEL: Record<ScanPreset, string> = {
  today: '오늘',
  yesterday: '어제',
  '3d': '3일',
  '7d': '7일',
};

export function OrdersPage() {
  const { orders } = useLoaderData() as LoaderData;
  const { revalidate } = useRevalidator();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<OrderStatus>('COLLECTED');
  const [bulkChanging, setBulkChanging] = useState(false);
  const [editingTrackingId, setEditingTrackingId] = useState<number | null>(null);
  const [trackingDraft, setTrackingDraft] = useState('');
  const [poDialog, setPoDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [collectAfter, setCollectAfter] = useState(() => toLocalDateInit(daysAgo(3)));
  const [collectBefore, setCollectBefore] = useState(() => toLocalDateInit(new Date()));
  const [collectPlatform, setCollectPlatform] = useState<CollectPlatform>('ALL');
  const [scanAfter, setScanAfter] = useState(() => toLocalDateInit(new Date()));
  const [scanBefore, setScanBefore] = useState(() => toLocalDateInit(new Date()));
  const [scanDryRun, setScanDryRun] = useState(false);
  const [scanResult, setScanResult] = useState<ScanInboxResult | null>(null);
  const [shipmentResult, setShipmentResult] = useState<
    Array<{ orderId: number; ok: boolean; reason?: string; externalOrderId?: string; productName?: string }> | null
  >(null);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  function setCollectQuickRange(preset: CollectPreset) {
    const today = toLocalDateInit(new Date());
    if (preset === 'today') {
      setCollectAfter(today); setCollectBefore(today);
    } else if (preset === '3d') {
      setCollectAfter(toLocalDateInit(daysAgo(3))); setCollectBefore(today);
    } else {
      setCollectAfter(toLocalDateInit(daysAgo(7))); setCollectBefore(today);
    }
  }

  function setScanQuickRange(preset: ScanPreset) {
    const today = toLocalDateInit(new Date());
    if (preset === 'today') {
      setScanAfter(today); setScanBefore(today);
    } else if (preset === 'yesterday') {
      const y = toLocalDateInit(daysAgo(1));
      setScanAfter(y); setScanBefore(y);
    } else if (preset === '3d') {
      setScanAfter(toLocalDateInit(daysAgo(3))); setScanBefore(today);
    } else {
      setScanAfter(toLocalDateInit(daysAgo(7))); setScanBefore(today);
    }
  }

  function isCollectPresetActive(preset: CollectPreset): boolean {
    const today = toLocalDateInit(new Date());
    if (preset === 'today') return collectAfter === today && collectBefore === today;
    if (preset === '3d') return collectAfter === toLocalDateInit(daysAgo(3)) && collectBefore === today;
    return collectAfter === toLocalDateInit(daysAgo(7)) && collectBefore === today;
  }

  function isScanPresetActive(preset: ScanPreset): boolean {
    const today = toLocalDateInit(new Date());
    if (preset === 'today') return scanAfter === today && scanBefore === today;
    if (preset === 'yesterday') {
      const y = toLocalDateInit(daysAgo(1));
      return scanAfter === y && scanBefore === y;
    }
    if (preset === '3d') return scanAfter === toLocalDateInit(daysAgo(3)) && scanBefore === today;
    return scanAfter === toLocalDateInit(daysAgo(7)) && scanBefore === today;
  }

  const STATUS_LABEL: Record<OrderStatus, string> = {
    COLLECTED: '수집완료',
    PO_SENT: '발주전송',
    TRACKING_RECEIVED: '송장수신',
    SHIPPED_TO_MALL: '출고확정',
    DONE: '완료',
    FAILED_PO: '발주실패',
    FAILED_PARSE: '파싱실패',
    FAILED_SHIP: '출고실패',
  };

  const visibleOrders = orders.filter((o) => o.status !== 'DONE');
  const selectedOrders = visibleOrders.filter((o) => selected.has(o.id));
  const allCollected = visibleOrders.filter((o) => o.status === 'COLLECTED');
  const allTrackingReceived = visibleOrders.filter((o) => o.status === 'TRACKING_RECEIVED');
  const hasSelection = selected.size > 0;
  const poTargets = hasSelection
    ? selectedOrders.filter((o) => o.status === 'COLLECTED')
    : allCollected;
  const shipTargets = hasSelection
    ? selectedOrders.filter((o) => o.status === 'TRACKING_RECEIVED')
    : allTrackingReceived;
  const totalAmount = selectedOrders.reduce((s, o) => s + o.totalPrice, 0);

  async function withErr(label: string, fn: () => Promise<void>) {
    try {
      await fn();
    } catch (e) {
      const err = e as ApiError;
      pushToast('error', `${label} 실패: ${err.message ?? '알 수 없는 오류'}`);
    }
  }

  function toggleSelected(id: number, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  }

  async function applyBulkStatus() {
    setBulkChanging(true);
    try {
      const ids = [...selected];
      const settled = await Promise.allSettled(
        ids.map((id) => api.patch(`/orders/${id}/status`, { status: bulkStatus })),
      );
      const ok = settled.filter((r) => r.status === 'fulfilled').length;
      const ng = settled.filter((r) => r.status === 'rejected').length;
      if (ng === 0) {
        pushToast('success', `${ok}건 상태를 "${STATUS_LABEL[bulkStatus]}"로 변경했습니다.`);
      } else {
        pushToast('error', `성공 ${ok}건, 실패 ${ng}건 — 실패 건은 송장번호를 확인해 주세요.`);
      }
      setSelected(new Set());
      revalidate();
    } finally {
      setBulkChanging(false);
    }
  }

  async function saveTracking(o: Order, value: string) {
    if (value === (o.trackingNumber ?? '')) return;
    await withErr('송장번호 저장', async () => {
      await api.patch(`/orders/${o.id}/tracking`, { trackingNumber: value });
      pushToast('success', value ? '송장번호를 저장했습니다.' : '송장번호를 삭제했습니다.');
      revalidate();
    });
  }

  async function runCollect() {
    setCollecting(true);
    try {
      const targets: Array<'NAVER' | 'COUPANG'> =
        collectPlatform === 'ALL' ? ['NAVER', 'COUPANG'] : [collectPlatform];
      const settled = await Promise.allSettled(
        targets.map((platform) => {
          const body: Record<string, unknown> = { platform };
          if (collectAfter) body.since = collectAfter;
          if (collectBefore) body.until = collectBefore;
          return api.post<{ inserted: number; skipped: number; failed: number }>(
            '/orders/collect',
            body,
          );
        }),
      );
      let inserted = 0, skipped = 0, failed = 0;
      const errors: string[] = [];
      settled.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          inserted += r.value.inserted;
          skipped += r.value.skipped;
          failed += r.value.failed;
        } else {
          const err = r.reason as ApiError;
          errors.push(`${targets[i]}: ${err.message ?? '알 수 없는 오류'}`);
        }
      });
      const label = collectPlatform === 'ALL' ? '전체 수집' : '수집';
      if (errors.length === targets.length) {
        pushToast('error', `${label} 실패 — ${errors.join(' · ')}`);
      } else if (errors.length > 0) {
        pushToast(
          'error',
          `${label} 부분 실패 — 신규 ${inserted}건, 업데이트 ${skipped}건, 실패 ${failed}건 · ${errors.join(' · ')}`,
        );
      } else {
        pushToast(
          inserted > 0 ? 'success' : 'info',
          `${label} 완료 — 신규 ${inserted}건, 업데이트 ${skipped}건, 실패 ${failed}건`,
        );
      }
      revalidate();
    } finally {
      setCollecting(false);
    }
  }

  async function runScan() {
    setScanning(true);
    try {
      const body: Record<string, unknown> = {};
      if (scanAfter) body.after = scanAfter;
      if (scanBefore) body.before = scanBefore;
      if (scanDryRun) body.dryRun = true;
      const res = await api.post<ScanInboxResult>('/fulfillment/inbox', body);
      setScanResult(res);
      const prefix = res.dryRun ? '[테스트] ' : '';
      pushToast(
        res.matched > 0 ? 'success' : 'info',
        `${prefix}스캔 완료 — 메시지 ${res.scanned}건, 매칭 ${res.matched}건, 미매칭 ${res.unmatched}건, 실패 ${res.failed}건`,
      );
      if (!scanDryRun) revalidate();
    } finally {
      setScanning(false);
    }
  }

  return (
    <div>
      <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
        <div className="shrink-0">
          <p className="label-overline">Step 1</p>
          <h1 className="mt-1 text-[28px] font-light tracking-[-0.01em] text-ink-950">주문</h1>
          <p className="mt-1 text-[13px] text-ink-500">
            선택 <span className="tabular text-ink-900">{selected.size}</span>건 · 합계{' '}
            <span className="tabular text-ink-900">{formatKRW(totalAmount)}</span>
          </p>
        </div>

        {/* ========= MOBILE TOOLBARS ========= */}
        <div className="space-y-4 lg:hidden">
          {/* Collect */}
          <section className="rounded-xl border border-ink-200 bg-paper p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-800">주문 수집</h2>
              <span className="text-xs text-ink-500">신규 주문을 가져옵니다</span>
            </div>
            <div className="mt-3 space-y-2.5">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ink-500">플랫폼</span>
                <select
                  value={collectPlatform}
                  onChange={(e) => setCollectPlatform(e.target.value as CollectPlatform)}
                  className="h-11 w-full rounded-lg border border-ink-200 bg-paper px-3 text-base text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
                  aria-label="수집 플랫폼"
                >
                  <option value="ALL">전체</option>
                  <option value="NAVER">네이버</option>
                  <option value="COUPANG">쿠팡</option>
                </select>
              </label>

              <div>
                <span className="mb-1 block text-xs font-medium text-ink-500">기간</span>
                <div className="grid grid-cols-3 gap-2">
                  {(['today', '3d', '7d'] as const).map((preset) => {
                    const active = isCollectPresetActive(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        className={[
                          'h-10 rounded-lg border text-sm font-medium transition-colors',
                          active
                            ? 'border-coral-500 bg-coral-50 text-coral-700'
                            : 'border-ink-200 bg-paper text-ink-700 active:bg-ink-100',
                        ].join(' ')}
                        onClick={() => setCollectQuickRange(preset)}
                      >
                        {COLLECT_PRESET_LABEL[preset]}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="date"
                    value={collectAfter}
                    onChange={(e) => setCollectAfter(e.target.value)}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 text-base text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
                    aria-label="수집 시작일"
                  />
                  <span className="text-sm text-ink-400">~</span>
                  <input
                    type="date"
                    value={collectBefore}
                    min={collectAfter}
                    onChange={(e) => setCollectBefore(e.target.value)}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 text-base text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
                    aria-label="수집 종료일"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                <button
                  type="button"
                  className="inline-flex h-12 items-center justify-center rounded-lg border border-ink-200 bg-paper text-base font-medium text-ink-800 active:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={collecting}
                  onClick={() => withErr('주문 수집', runCollect)}
                >
                  {collecting ? '수집 중…' : '주문 수집'}
                </button>
                <button
                  type="button"
                  className="inline-flex h-12 items-center justify-center rounded-lg bg-coral-500 text-base font-semibold text-white active:bg-coral-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={poTargets.length === 0}
                  onClick={() => setPoDialog(true)}
                >
                  발주 메일 ({poTargets.length}{hasSelection ? ' · 선택' : ''})
                </button>
              </div>
            </div>
          </section>

          {/* Scan */}
          <section className="rounded-xl border border-ink-200 bg-paper p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-800">송장 회신 스캔</h2>
              <label className="flex cursor-pointer select-none items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={scanDryRun}
                  onChange={(e) => setScanDryRun(e.target.checked)}
                  className="h-4 w-4 accent-amber-500"
                />
                <span className="text-xs font-medium text-amber-700">테스트 모드</span>
              </label>
            </div>
            <div className="mt-3 space-y-2.5">
              <div>
                <span className="mb-1 block text-xs font-medium text-ink-500">기간</span>
                <div className="grid grid-cols-4 gap-2">
                  {(['today', 'yesterday', '3d', '7d'] as const).map((preset) => {
                    const active = isScanPresetActive(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        className={[
                          'h-10 rounded-lg border text-sm font-medium transition-colors',
                          active
                            ? 'border-coral-500 bg-coral-50 text-coral-700'
                            : 'border-ink-200 bg-paper text-ink-700 active:bg-ink-100',
                        ].join(' ')}
                        onClick={() => setScanQuickRange(preset)}
                      >
                        {SCAN_PRESET_LABEL[preset]}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="date"
                    value={scanAfter}
                    onChange={(e) => setScanAfter(e.target.value)}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 text-base text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
                    aria-label="스캔 시작일"
                  />
                  <span className="text-sm text-ink-400">~</span>
                  <input
                    type="date"
                    value={scanBefore}
                    min={scanAfter}
                    onChange={(e) => setScanBefore(e.target.value)}
                    className="h-11 min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 text-base text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
                    aria-label="스캔 종료일"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                <button
                  type="button"
                  className={[
                    'inline-flex h-12 items-center justify-center rounded-lg border bg-paper text-base font-medium active:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60',
                    scanDryRun
                      ? 'border-amber-400 text-amber-700'
                      : 'border-ink-200 text-ink-800',
                  ].join(' ')}
                  disabled={scanning}
                  onClick={() => withErr('회신 스캔', runScan)}
                >
                  {scanning ? '스캔 중…' : scanDryRun ? '테스트 스캔' : '회신 스캔'}
                </button>
                <button
                  type="button"
                  className="inline-flex h-12 items-center justify-center rounded-lg bg-coral-500 text-base font-semibold text-white active:bg-coral-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={shipTargets.length === 0}
                  onClick={() => setConfirmDialog(true)}
                >
                  송장 전송 ({shipTargets.length}{hasSelection ? ' · 선택' : ''})
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* ========= DESKTOP TOOLBARS ========= */}
        <div className="hidden lg:flex lg:flex-col lg:items-end lg:gap-2">
          {/* Row 1: 주문 수집 + 발주 메일 */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-md border border-ink-200 bg-ink-50 px-2 py-1">
              <select
                value={collectPlatform}
                onChange={(e) => setCollectPlatform(e.target.value as CollectPlatform)}
                className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-coral-500"
                aria-label="수집 플랫폼"
              >
                <option value="ALL">전체</option>
                <option value="NAVER">네이버</option>
                <option value="COUPANG">쿠팡</option>
              </select>
              <span className="mx-1 text-ink-300">|</span>
              <span className="mr-0.5 text-xs text-ink-400">기간</span>
              {(['today', '3d', '7d'] as const).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="rounded px-2 py-0.5 text-xs text-ink-600 transition-colors hover:bg-ink-200"
                  onClick={() => setCollectQuickRange(preset)}
                >
                  {COLLECT_PRESET_LABEL[preset]}
                </button>
              ))}
              <span className="mx-1 text-ink-300">|</span>
              <input
                type="date"
                value={collectAfter}
                onChange={(e) => setCollectAfter(e.target.value)}
                className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-coral-500"
                aria-label="수집 시작일"
              />
              <span className="text-xs text-ink-400">~</span>
              <input
                type="date"
                value={collectBefore}
                min={collectAfter}
                onChange={(e) => setCollectBefore(e.target.value)}
                className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-coral-500"
                aria-label="수집 종료일"
              />
              <button
                type="button"
                className="btn-secondary ml-1"
                disabled={collecting}
                onClick={() => withErr('주문 수집', runCollect)}
              >
                {collecting ? '수집 중…' : '주문 수집'}
              </button>
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={poTargets.length === 0}
              title={
                poTargets.length === 0
                  ? hasSelection
                    ? '선택한 주문 중 수집완료 상태가 없습니다.'
                    : '수집완료 상태 주문이 없습니다.'
                  : ''
              }
              onClick={() => setPoDialog(true)}
            >
              발주 메일 보내기 ({poTargets.length}{hasSelection ? ' · 선택' : ''})
            </button>
          </div>
          {/* Row 2: 송장회신스캔 + 송장 전송 */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-md border border-ink-200 bg-ink-50 px-2 py-1">
              <span className="mr-0.5 text-xs text-ink-400">기간</span>
              {(['today', 'yesterday', '3d', '7d'] as const).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="rounded px-2 py-0.5 text-xs text-ink-600 transition-colors hover:bg-ink-200"
                  onClick={() => setScanQuickRange(preset)}
                >
                  {SCAN_PRESET_LABEL[preset]}
                </button>
              ))}
              <span className="mx-1 text-ink-300">|</span>
              <input
                type="date"
                value={scanAfter}
                onChange={(e) => setScanAfter(e.target.value)}
                className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-coral-500"
                aria-label="스캔 시작일"
              />
              <span className="text-xs text-ink-400">~</span>
              <input
                type="date"
                value={scanBefore}
                min={scanAfter}
                onChange={(e) => setScanBefore(e.target.value)}
                className="rounded border border-ink-200 bg-paper px-1.5 py-0.5 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-coral-500"
                aria-label="스캔 종료일"
              />
              <label className="ml-1 flex cursor-pointer select-none items-center gap-1">
                <input
                  type="checkbox"
                  checked={scanDryRun}
                  onChange={(e) => setScanDryRun(e.target.checked)}
                  className="accent-amber-500"
                />
                <span className="text-xs text-amber-700">테스트</span>
              </label>
              <button
                type="button"
                className={scanDryRun ? 'btn-secondary ml-1 border-amber-400 text-amber-700' : 'btn-secondary ml-1'}
                disabled={scanning}
                onClick={() => withErr('회신 스캔', runScan)}
              >
                {scanning ? '스캔 중…' : scanDryRun ? '테스트 스캔' : '송장 회신 스캔'}
              </button>
            </div>
            <button
              type="button"
              className="btn-primary"
              disabled={shipTargets.length === 0}
              title={
                shipTargets.length === 0
                  ? hasSelection
                    ? '선택한 주문 중 송장수신 상태가 없습니다.'
                    : '송장수신 상태 주문이 없습니다.'
                  : ''
              }
              onClick={() => setConfirmDialog(true)}
            >
              송장 전송 ({shipTargets.length}{hasSelection ? ' · 선택' : ''})
            </button>
          </div>
        </div>
      </header>

      {/* Bulk action bar + status counts */}
      <div className="mt-4 rounded-lg border border-ink-200 bg-ink-50">
        {/* Mobile: stacked layout */}
        <div className="space-y-2.5 p-3 sm:hidden">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-sm font-medium text-ink-700">일괄 상태</span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value as OrderStatus)}
              disabled={bulkChanging}
              className="h-10 min-w-0 flex-1 rounded-md border border-ink-200 bg-paper px-3 text-sm text-ink-800 focus:border-coral-500 focus:outline-none focus:ring-1 focus:ring-coral-500"
            >
              {(['COLLECTED','PO_SENT','TRACKING_RECEIVED','DONE','FAILED_PO','FAILED_PARSE','FAILED_SHIP'] as OrderStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="inline-flex h-11 items-center justify-center rounded-md border border-ink-200 bg-paper text-sm font-medium text-ink-800 active:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={selected.size === 0 || bulkChanging}
              onClick={applyBulkStatus}
            >
              {bulkChanging ? '변경 중…' : `변경 (${selected.size})`}
            </button>
            <button
              type="button"
              className="inline-flex h-11 items-center justify-center rounded-md bg-red-600 text-sm font-semibold text-white active:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={selected.size === 0 || deleting || bulkChanging}
              onClick={() => setDeleteDialog(true)}
            >
              삭제 ({selected.size})
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-ink-200 pt-2.5 text-sm">
            {(['COLLECTED', 'PO_SENT', 'TRACKING_RECEIVED'] as OrderStatus[]).map((s) => (
              <span key={s} className="text-ink-500">
                {STATUS_LABEL[s]} <span className="font-semibold text-ink-800">{orders.filter((o) => o.status === s).length}</span>건
              </span>
            ))}
          </div>
        </div>

        {/* Desktop: inline layout */}
        <div className="hidden flex-wrap items-center gap-2 px-3 py-2 sm:flex">
          <span className="text-sm font-medium text-ink-600">일괄 상태 변경</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as OrderStatus)}
            disabled={bulkChanging}
            className="rounded border border-ink-200 bg-paper px-2 py-1 text-sm text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500"
          >
            {(['COLLECTED','PO_SENT','TRACKING_RECEIVED','DONE','FAILED_PO','FAILED_PARSE','FAILED_SHIP'] as OrderStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn-secondary"
            disabled={selected.size === 0 || bulkChanging}
            onClick={applyBulkStatus}
          >
            {bulkChanging ? '변경 중…' : `선택 ${selected.size}건 변경`}
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={selected.size === 0 || deleting || bulkChanging}
            onClick={() => setDeleteDialog(true)}
          >
            선택 삭제 ({selected.size})
          </button>
          <div className="ml-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-l border-ink-200 pl-3 text-xs">
            {(['COLLECTED', 'PO_SENT', 'TRACKING_RECEIVED'] as OrderStatus[]).map((s) => (
              <span key={s} className="text-ink-500">
                {STATUS_LABEL[s]} <span className="font-semibold text-ink-800">{orders.filter((o) => o.status === s).length}</span>건
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden">
        {visibleOrders.length > 0 && (
          <label className="mt-3 flex cursor-pointer items-center justify-between rounded-lg border border-ink-200 bg-paper px-3 py-2.5 shadow-sm">
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="모두 선택"
                checked={visibleOrders.length > 0 && selected.size === visibleOrders.length}
                onChange={(e) =>
                  setSelected(e.target.checked ? new Set(visibleOrders.map((o) => o.id)) : new Set())
                }
                className="h-5 w-5 shrink-0 accent-brand-600"
              />
              <span className="text-sm font-medium text-ink-800">전체 선택</span>
            </span>
            <span className="text-sm text-ink-500">
              <span className="font-semibold text-ink-700">{selected.size}</span> / {visibleOrders.length}
            </span>
          </label>
        )}

        <div className="mt-2 space-y-1.5">
          {visibleOrders.length === 0 ? (
            <div className="rounded-lg border border-ink-200 bg-paper px-4 py-10 text-center text-sm text-ink-500">
              주문이 없습니다. 상단 "주문 수집"으로 새 주문을 가져오세요.
            </div>
          ) : (
            visibleOrders.map((o) => (
              <article
                key={o.id}
                role="button"
                tabIndex={0}
                onClick={() => setDetailOrder(o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailOrder(o); } }}
                className={[
                  'cursor-pointer rounded-lg border bg-paper px-3 py-3 shadow-sm active:bg-ink-50',
                  selected.has(o.id) ? 'border-coral-400 ring-1 ring-coral-100' : 'border-ink-200',
                ].join(' ')}
              >
                {/* Row 1: checkbox · badges  ·······  주문일시 ›  */}
                <div className="flex items-center gap-2">
                  <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <input
                      type="checkbox"
                      aria-label={`주문 ${o.externalOrderId} 선택`}
                      checked={selected.has(o.id)}
                      onChange={(e) => toggleSelected(o.id, e.target.checked)}
                      className="h-5 w-5 accent-brand-600"
                    />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                    <StatusBadge status={o.status} />
                    <ChannelBadge platform={o.platform} channelName={o.channelName} />
                    {!!o.isAddedProduct && (
                      <span className="rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700">
                        추가
                      </span>
                    )}
                    {!!o.addedProductUnmatched && (
                      <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        매칭없음
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 whitespace-nowrap text-right text-xs text-ink-500">
                    {formatDateTime(o.orderedAt)}
                  </span>
                  <span className="shrink-0 text-sm text-ink-400" aria-hidden="true">›</span>
                </div>

                {/* Row 2: 상품명 · 합계 */}
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="min-w-0 flex-1 truncate text-base font-semibold leading-snug text-ink-900" title={o.productName}>
                    {o.productName}
                  </p>
                  <span className="shrink-0 text-base font-semibold text-ink-900">
                    {formatKRW(o.totalPrice)}
                  </span>
                </div>

                {/* Row 3-5: 주문자 / 주문번호 / 수량 (라벨형) */}
                <dl className="mt-2 space-y-0.5 text-sm leading-snug text-ink-700">
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">주문자:</dt>
                    <dd className="min-w-0 flex-1 truncate font-medium text-ink-800">{o.buyerName}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">주문번호:</dt>
                    <dd className="min-w-0 flex-1 truncate font-mono text-ink-700" title={o.externalOrderId}>
                      {o.externalOrderId}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="shrink-0 text-ink-500">수량:</dt>
                    <dd className="font-medium text-ink-800">{o.quantity}개</dd>
                  </div>
                </dl>

                {/* Row 6: 송장 inline edit */}
                <div className="mt-2.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="shrink-0 text-sm text-ink-500">송장:</span>
                  {editingTrackingId === o.id ? (
                    <input
                      autoFocus
                      type="text"
                      inputMode="numeric"
                      value={trackingDraft}
                      onChange={(e) => setTrackingDraft(e.target.value.replace(/\D/g, ''))}
                      placeholder="송장 입력"
                      className="h-10 min-w-0 flex-1 rounded-md border-2 border-coral-400 bg-paper px-2.5 font-mono text-base text-ink-900 focus:outline-none focus:ring-1 focus:ring-coral-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') setEditingTrackingId(null);
                      }}
                      onBlur={async () => {
                        setEditingTrackingId(null);
                        await saveTracking(o, trackingDraft.trim());
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="flex h-10 min-w-0 flex-1 items-center rounded-md border border-ink-200 bg-paper px-2.5 text-left font-mono text-base text-ink-800 active:bg-ink-50"
                      onClick={() => { setTrackingDraft(o.trackingNumber ?? ''); setEditingTrackingId(o.id); }}
                    >
                      {o.trackingNumber ?? <span className="font-sans text-sm text-ink-400">탭하여 입력</span>}
                    </button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </div>

      {/* Desktop table */}
      <div className="mt-2 hidden overflow-hidden rounded-lg border border-ink-200 bg-paper md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <colgroup>
              <col className="w-10" />
              <col className="w-28" />
              <col className="w-20" />
              <col className="w-36" />
              <col />
              <col className="w-36" />
              <col className="w-48" />
              <col className="w-14" />
              <col className="w-24" />
              <col className="w-44" />
              <col className="w-36" />
            </colgroup>
            <thead className="bg-ink-50 text-ink-600">
              <tr>
                <th className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    aria-label="모두 선택"
                    checked={visibleOrders.length > 0 && selected.size === visibleOrders.length}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(visibleOrders.map((o) => o.id)) : new Set())
                    }
                  />
                </th>
                <th className="px-3 py-2 text-center">상태</th>
                <th className="px-3 py-2 text-center">채널</th>
                <th className="px-3 py-2 text-center">주문번호</th>
                <th className="px-3 py-2 text-center">상품</th>
                <th className="px-3 py-2 text-center">수령인</th>
                <th className="px-3 py-2 text-center">주소</th>
                <th className="px-3 py-2 text-center">수량</th>
                <th className="px-3 py-2 text-center">합계</th>
                <th className="px-3 py-2 text-center">주문일시</th>
                <th className="px-3 py-2 text-center">송장번호</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-sm text-ink-500">
                    주문이 없습니다. 우상단 "주문 수집"으로 새 주문을 가져오세요.
                  </td>
                </tr>
              )}
              {visibleOrders.map((o) => (
                <tr key={o.id} className="border-t border-ink-100 hover:bg-ink-50">
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`주문 ${o.externalOrderId} 선택`}
                      checked={selected.has(o.id)}
                      onChange={(e) => toggleSelected(o.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <ChannelBadge platform={o.platform} channelName={o.channelName} />
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs text-ink-700">{o.externalOrderId}</td>
                  <td className="px-3 py-2 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {!!o.isAddedProduct && (
                        <span className="shrink-0 rounded border border-sky-300 bg-sky-50 px-1 py-0.5 text-xs font-medium text-sky-700">추가상품</span>
                      )}
                      {!!o.addedProductUnmatched && (
                        <span className="shrink-0 rounded border border-amber-300 bg-amber-50 px-1 py-0.5 text-xs font-medium text-amber-700">매칭없음</span>
                      )}
                      <span className="font-medium truncate" title={o.productName}>{o.productName}</span>
                    </div>
                    {o.productOption && (
                      <div className="text-xs text-ink-500 truncate" title={o.productOption}>{o.productOption}</div>
                    )}
                    {(o.pCode || o.productId) && (
                      <div className="text-xs text-ink-400 font-mono truncate">
                        {[o.pCode && `품번:${o.pCode}`, o.productId && `채널품번:${o.productId}`].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">{o.recipientName}</td>
                  <td className="px-3 py-2 text-center text-xs text-ink-600">{o.recipientAddress}</td>
                  <td className="px-3 py-2 text-center">{o.quantity}</td>
                  <td className="px-3 py-2 text-center">{formatKRW(o.totalPrice)}</td>
                  <td className="px-3 py-2 text-center text-xs text-ink-500">{formatDateTime(o.orderedAt)}</td>
                  <td className="px-3 py-2 text-center">
                    {editingTrackingId === o.id ? (
                      <input
                        autoFocus
                        type="text"
                        value={trackingDraft}
                        onChange={(e) => setTrackingDraft(e.target.value.replace(/\D/g, ''))}
                        placeholder="송장 입력"
                        className="w-full rounded border border-coral-400 px-1.5 py-0.5 text-xs font-mono text-ink-800 focus:outline-none focus:ring-1 focus:ring-coral-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') { setEditingTrackingId(null); }
                        }}
                        onBlur={async () => {
                          setEditingTrackingId(null);
                          await saveTracking(o, trackingDraft.trim());
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        title="클릭하여 송장번호 입력"
                        className="w-full font-mono text-xs text-ink-600 hover:bg-ink-100 rounded px-1 py-0.5 text-center"
                        onClick={() => { setTrackingDraft(o.trackingNumber ?? ''); setEditingTrackingId(o.id); }}
                      >
                        {o.trackingNumber ?? <span className="text-ink-300">클릭</span>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <POPreviewDialog
        open={poDialog}
        orderIds={hasSelection ? poTargets.map((o) => o.id) : undefined}
        onCancel={() => setPoDialog(false)}
        onConfirm={async (ids) => {
          setPoDialog(false);
          await withErr('발주 메일 발송', async () => {
            const res = await api.post<{ batchCode: string; itemCount: number }>(
              '/fulfillment/po',
              { orderIds: ids },
            );
            pushToast('success', `발주 ${res.batchCode} 발송 완료 (${res.itemCount}건)`);
            setSelected(new Set());
            revalidate();
          });
        }}
      />

      <ScanResultDialog
        open={scanResult !== null}
        result={scanResult}
        onClose={() => setScanResult(null)}
      />

      <ConfirmDialog
        open={deleteDialog}
        title="주문을 삭제할까요?"
        description={
          <div>
            <p>
              선택한 <strong>{selected.size}</strong>건을 삭제합니다. 이벤트 이력도 함께 삭제되며 되돌릴 수 없습니다.
            </p>
            <p className="mt-1 text-ink-500">발주 메일을 보낸 내역은 배치 로그에 그대로 남습니다.</p>
          </div>
        }
        confirmLabel={`${selected.size}건 삭제`}
        destructive
        onCancel={() => setDeleteDialog(false)}
        onConfirm={async () => {
          setDeleteDialog(false);
          setDeleting(true);
          await withErr('주문 삭제', async () => {
            const res = await api.delete<{ deleted: number }>('/orders', { ids: [...selected] });
            pushToast('success', `${res.deleted}건을 삭제했습니다.`);
            setSelected(new Set());
            revalidate();
          });
          setDeleting(false);
        }}
      />

      <ConfirmDialog
        open={confirmDialog}
        title="송장 전송을 진행할까요?"
        description={
          <div>
            <p>
              {hasSelection ? '선택한 ' : ''}
              <strong>{shipTargets.length}</strong>건을 네이버에 송장 전송합니다.
            </p>
            <p className="mt-1 text-ink-500">취소할 수 없습니다.</p>
          </div>
        }
        confirmLabel="송장 전송"
        destructive
        onCancel={() => setConfirmDialog(false)}
        onConfirm={async () => {
          setConfirmDialog(false);
          await withErr('출고 확정', async () => {
            const ids = shipTargets.map((o) => o.id);
            const res = await api.post<{
              results: Array<{ orderId: number; ok: boolean; reason?: string }>;
            }>('/shipments/confirm', { orderIds: ids });
            const orderMap = new Map(shipTargets.map((o) => [o.id, o]));
            const enriched = res.results.map((r) => {
              const order = orderMap.get(r.orderId);
              return { ...r, externalOrderId: order?.externalOrderId, productName: order?.productName };
            });
            const ok = enriched.filter((r) => r.ok).length;
            const ng = enriched.length - ok;
            pushToast(
              ng === 0 ? 'success' : ok === 0 ? 'error' : 'error',
              `출고 확정 — 성공 ${ok}건${ng > 0 ? ` / 실패 ${ng}건` : ''}`,
            );
            setShipmentResult(enriched);
            setSelected(new Set());
            revalidate();
          });
        }}
      />

      <ShipmentResultDialog
        open={shipmentResult !== null}
        results={shipmentResult ?? []}
        onClose={() => setShipmentResult(null)}
      />

      <OrderDetailDialog
        open={detailOrder !== null}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
      />
    </div>
  );
}
